import { z } from 'zod';
import { optionalUuidString } from '../zod-helpers/index.js';
import { bomWarehouseIdsSchema } from './bom-warehouse-ids-schema.js';
import { BOM_CONSTRAINTS } from './constraints.js';
import { checkBomDescription, checkBomWarehousesRequiredOnCreate } from './rules.js';
export const bomFormFieldsSchema = z.object({
    num: z.string().max(BOM_CONSTRAINTS.num.maxLength),
    description: z.string().max(BOM_CONSTRAINTS.description.maxLength),
    brandId: optionalUuidString('Invalid brand selection.'),
    partId: optionalUuidString('Invalid part selection.'),
    configurable: z.boolean(),
    pickFromLocation: z.boolean(),
    note: z.string().max(BOM_CONSTRAINTS.note.maxLength),
    warehouseLocationGroupIds: bomWarehouseIdsSchema,
});
// Shares BomFormValues with create; passing num or brandId through is a type error, not a footgun.
// requireWarehouses is for an edit that would send warehouseLocationGroupIds: [] (user cleared the
// last visible warehouse). Hidden or already-empty loads omit the key instead — those must not trip this floor.
export function createBomEditFormSchema(options = {}) {
    return bomFormFieldsSchema.superRefine((values, context) => {
        for (const issue of checkBomDescription(values)) {
            context.addIssue({ code: 'custom', message: issue.message, path: [issue.path] });
        }
        if (!options.requireWarehouses)
            return;
        for (const issue of checkBomWarehousesRequiredOnCreate(values)) {
            context.addIssue({ code: 'custom', message: issue.message, path: [issue.path] });
        }
    });
}
export const bomEditFormSchema = createBomEditFormSchema();
export const emptyBomFormValues = () => ({
    num: '',
    description: '',
    brandId: '',
    partId: '',
    configurable: false,
    pickFromLocation: false,
    note: '',
    warehouseLocationGroupIds: [],
});
export function bomToEditFormState(bom) {
    return {
        num: bom.num,
        description: bom.description,
        // Permission-hidden renders as unset, same as a hidden output part or warehouse.
        brandId: bom.brand?.id ?? '',
        partId: bom.part?.[0]?.id ?? '',
        // Not sent by the edit form (see BomEditableFields) — kept so this satisfies BomFormValues.
        configurable: bom.configurable,
        pickFromLocation: bom.pickFromLocation,
        note: bom.note ?? '',
        // Drop permission-hidden warehouses so save doesn't overwrite them.
        warehouseLocationGroupIds: bom.bomToLocation.flatMap((entry) => entry.locationGroup ? [entry.locationGroup.id] : []),
    };
}
// A null warehouse means permission-hidden, not empty — saving must not clear it.
export function bomHasHiddenWarehouse(bom) {
    return bom.bomToLocation.some((entry) => !entry.locationGroup);
}
//# sourceMappingURL=update-form.js.map